import { z } from "zod";

// ---------------------------------------------------------------------------
// Starter questions
//
// AI-generated quick-prompt suggestions for the dashboard command bar —
// concrete questions a user would want to ask the coach, grounded in what the
// engine already knows about the business (memory, company profile, team
// state). One document per business at:
//   Businesses/{businessId}/meta/starterQuestions
//
// Regenerated by the nightly intelligence run and on demand when stale.
// Distinct from `insight.ts`: insights are findings pushed at the user;
// starter questions are invitations to pull a conversation.
// ---------------------------------------------------------------------------

export const STARTER_QUESTION_LABEL_MAX = 44;
export const STARTER_QUESTIONS_MAX = 2;

/** What the AI returns per question. */
export const AiStarterQuestion = z.object({
  /** Short chip text shown in the UI. */
  label: z.string().min(1).max(STARTER_QUESTION_LABEL_MAX),
  /** Full question sent to the coach when the chip is clicked. */
  prompt: z.string().min(1).max(400),
});
export type AiStarterQuestion = z.infer<typeof AiStarterQuestion>;

export const AiStarterQuestionBatch = z.object({
  questions: z.array(AiStarterQuestion).max(STARTER_QUESTIONS_MAX),
});
export type AiStarterQuestionBatch = z.infer<typeof AiStarterQuestionBatch>;

// ---------------------------------------------------------------------------
// Firestore document shape
// ---------------------------------------------------------------------------

export interface StarterQuestionsDoc {
  questions: AiStarterQuestion[];
  generatedAt: string | null;
  promptVersion: number | null;
  modelId: string | null;
  contextFingerprint: string | null;
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const GetStarterQuestionsInput = z.object({
  businessId: z.string().min(1),
});
export type GetStarterQuestionsInput = z.infer<typeof GetStarterQuestionsInput>;

export const RefreshStarterQuestionsInput = z.object({
  businessId: z.string().min(1),
});
export type RefreshStarterQuestionsInput = z.infer<
  typeof RefreshStarterQuestionsInput
>;

/** Staleness threshold for the on-demand refresh path (ms). */
export const STARTER_QUESTIONS_STALE_MS = 6 * 60 * 60 * 1000; // 6h
